import React, { useCallback, useEffect, useRef, useState } from 'react';
import Sidebar from '../../../components/Sidebar';
import AppDashboardNav from '../AppDashboardNav';
import {
  HiMenu,
  HiSearch,
  HiPlus,
  HiChevronLeft,
  HiChevronRight,
  HiChevronDown,
  HiInformationCircle,
} from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import Toast from '../../../components/toaster';
import { uploadImage } from '../../../services/assetService';
import {
  fetchComingSoonFeatureList,
  createComingSoonFeature,
  updateComingSoonFeature,
  deactivateComingSoonFeature,
  fetchComingSoonFeatureRegistrations,
} from '../../../services/comingSoonFeatureService';

const PAGE_SIZE = 50;
const HTTP_URL_RE = /^https?:\/\/.+/i;
const normalizeImageUrl = (value) => String(value || '').trim();

const ComingSoonImageField = ({
  id,
  value,
  error,
  onChange,
  onClear,
  onUploadingChange,
  disabled = false,
  resetKey = 0,
}) => {
  const [imageSource, setImageSource] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef(null);
  const trimmed = normalizeImageUrl(value);
  const canPreview = trimmed && HTTP_URL_RE.test(trimmed) && !error;

  useEffect(() => {
    setImageSource('url');
    setUploading(false);
    setUploadError('');
    setPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [resetKey]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [trimmed]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const switchSource = (next) => {
    if (uploading || disabled) return;
    setImageSource(next);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || uploading || disabled) return;
    setUploading(true);
    setUploadError('');
    try {
      onChange(await uploadImage(file));
    } catch (err) {
      setUploadError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled) return;
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClear();
  };

  const showClear = Boolean(trimmed) || Boolean(uploadError);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
          Image <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 group relative">
            <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
            <span
              className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                imageSource === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'
              }`}
            >
              {imageSource === 'url'
                ? 'Switch left to choose a file from your device'
                : 'Switch right to paste an image URL.'}
            </span>
          </div>
          <button
            type="button"
            aria-label={imageSource === 'url' ? 'Switch to file upload' : 'Switch to image URL'}
            disabled={uploading || disabled}
            onClick={() => switchSource(imageSource === 'url' ? 'file' : 'url')}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
              imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {imageSource === 'url' ? (
        <div>
          <div className="flex gap-2">
            <input
              id={id}
              type="url"
              value={value}
              disabled={uploading || disabled}
              onChange={(event) => {
                setUploadError('');
                onChange(event.target.value);
              }}
              placeholder="https://…"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 ${
                error ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">HTTP/HTTPS URL. Clear removes a saved image.</p>
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              id={`${id}-file`}
              type="file"
              accept="image/*"
              disabled={uploading || disabled}
              onChange={handleFileSelect}
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                uploadError || error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          {uploading && (
            <div className="mt-2 flex items-center gap-2" role="status" aria-live="polite">
              <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-xs text-gray-600">Uploading image...</span>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">Image file. Uses the shared Admin upload helper.</p>
        </div>
      )}

      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
      {error && !uploadError && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {canPreview && !previewFailed && !uploading && (
        <div className="mt-2">
          <img
            src={trimmed}
            alt="Coming soon preview"
            className="h-20 w-20 object-cover rounded-full border border-gray-200 bg-gray-50"
            onError={() => setPreviewFailed(true)}
          />
        </div>
      )}
      {canPreview && previewFailed && !uploading && (
        <p className="mt-2 text-xs text-amber-700">Preview unavailable — URL may be broken.</p>
      )}
    </div>
  );
};

const emptyForm = () => ({
  title: '',
  description: '',
  image: '',
  is_active: true,
});

const validateForm = (form) => {
  const errors = {};
  if (!String(form.title || '').trim()) errors.title = 'Title is required';
  if (!String(form.description || '').trim()) errors.description = 'Description is required';
  if (!String(form.image || '').trim()) errors.image = 'Image is required';
  return errors;
};

const StatusBadge = ({ isActive }) => (
  <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${isActive ? 'bg-[#9FF7C9]/30 text-[#60BC7C]' : 'bg-[#FFF0F0] text-[#F76464]'}`}>
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="40" stroke="#A855F7" strokeWidth="6" strokeLinecap="round" strokeDasharray="60 200" fill="none" />
      </svg>
    </div>
  </div>
);

const formatWhen = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const ComingSoonTab = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [saving, setSaving] = useState(false);
  const [imageFieldResetKey, setImageFieldResetKey] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [itemToDeactivate, setItemToDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [registrationsById, setRegistrationsById] = useState({});
  const [loadingRegsId, setLoadingRegsId] = useState(null);
  const searchTimerRef = useRef(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 0;

  const loadData = useCallback(async (searchText = '', page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchComingSoonFeatureList(page, PAGE_SIZE, searchText);
      const items = Array.isArray(res?.data) ? res.data : [];
      setData(items);
      setTotalCount(res?.totalCount ?? items.length);
    } catch (err) {
      setError(err.message || 'Error loading coming soon features.');
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => {
    loadData(debouncedSearch, currentPage);
  }, [debouncedSearch, currentPage, loadData]);

  const openAdd = () => {
    setFormData(emptyForm());
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(null);
    setIsEditing(false);
    setImageFieldResetKey((key) => key + 1);
    setUploadingImage(false);
    setShowFormModal(true);
  };

  const openEdit = (item) => {
    setFormData({
      title: item.title || '',
      description: item.description || '',
      image: item.image || '',
      is_active: item.is_active !== false,
    });
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(item);
    setIsEditing(true);
    setImageFieldResetKey((key) => key + 1);
    setUploadingImage(false);
    setShowFormModal(true);
  };

  const closeForm = () => {
    setShowFormModal(false);
    setFormData(emptyForm());
    setSelectedItem(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const errs = validateForm(formData);
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }
    const payload = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      image: formData.image.trim(),
      is_active: formData.is_active,
    };
    setSaving(true);
    setFormGeneralError('');
    try {
      if (isEditing) {
        await updateComingSoonFeature({ coming_soon_feature_id: selectedItem._id, ...payload });
        Toast.success('Coming soon feature updated');
      } else {
        await createComingSoonFeature(payload);
        Toast.success('Coming soon feature created');
      }
      closeForm();
      await loadData(debouncedSearch, isEditing ? currentPage : 1);
      if (!isEditing) setCurrentPage(1);
    } catch (err) {
      setFormGeneralError(err.message || 'Failed to save coming soon feature');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeactivate = async () => {
    if (!itemToDeactivate) return;
    setDeactivating(true);
    try {
      await deactivateComingSoonFeature(itemToDeactivate._id);
      Toast.success('Coming soon feature deactivated');
      setShowDeactivateModal(false);
      setItemToDeactivate(null);
      await loadData(debouncedSearch, currentPage);
    } catch (err) {
      Toast.error(err.message || 'Failed to deactivate');
    } finally {
      setDeactivating(false);
    }
  };

  const toggleExpand = async (item) => {
    const id = item._id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (registrationsById[id]) return;
    setLoadingRegsId(id);
    try {
      const res = await fetchComingSoonFeatureRegistrations(id);
      setRegistrationsById((prev) => ({ ...prev, [id]: Array.isArray(res?.data) ? res.data : [] }));
    } catch (err) {
      Toast.error(err.message || 'Failed to load notify list');
      setRegistrationsById((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setLoadingRegsId(null);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/50">
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">App Dashboard</h1>
            </div>
          </div>
          <AppDashboardNav />

          <div className="mb-4 flex flex-col md:flex-row md:justify-between gap-3">
            <div className="relative w-full md:w-64">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search coming soon..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#ECECEC] rounded-xl focus:outline-none bg-white"
              />
            </div>
            <button onClick={openAdd} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl bg-brand hover:opacity-90 self-end">
              <HiPlus className="w-5 h-5" />
              Add Coming soon
            </button>
          </div>

          {isLoading ? (
            <FullPageSpinner />
          ) : totalCount === 0 && !error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No coming soon features yet</h3>
                <p className="text-sm text-gray-500 mb-6">Add the first card for the new home Coming soon rail.</p>
                <button onClick={openAdd} className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl bg-brand">
                  <HiPlus className="w-4 h-4" /> Add Coming soon
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
              {error ? (
                <div className="px-4 py-6 text-center text-red-500">{error}</div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr>
                        <th className="w-10 px-2 py-3" />
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Image</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Title</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Description</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Notified</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.map((item) => {
                        const open = expandedId === item._id;
                        const regs = registrationsById[item._id] || [];
                        return (
                          <React.Fragment key={item._id}>
                            <tr
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => toggleExpand(item)}
                            >
                              <td className="px-2 py-3">
                                <span className="inline-flex p-1 rounded-lg" title="Notify list">
                                  {open ? <HiChevronDown className="w-4 h-4" /> : <HiChevronRight className="w-4 h-4" />}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {item.image ? (
                                  <img src={item.image} alt="" className="w-12 h-10 rounded-lg object-cover bg-gray-100" />
                                ) : (
                                  <div className="w-12 h-10 rounded-lg bg-gray-100" />
                                )}
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-800">{item.title}</td>
                              <td className="px-4 py-3 hidden md:table-cell text-gray-600">
                                {(item.description || '').length > 60 ? `${item.description.slice(0, 60)}…` : item.description}
                              </td>
                              <td className="px-4 py-3 text-gray-600">{item.notify_count ?? 0}</td>
                              <td className="px-4 py-3"><StatusBadge isActive={item.is_active} /></td>
                              <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                                <div className="flex gap-2">
                                  <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg" style={{ backgroundColor: '#F2E2F7' }}>
                                    <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                                  </button>
                                  <button
                                    onClick={() => { setItemToDeactivate(item); setShowDeactivateModal(true); }}
                                    className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg"
                                    title="Deactivate"
                                  >
                                    <MdDelete className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {open && (
                              <tr>
                                <td colSpan={7} className="bg-[#FDF6EF] px-6 py-4">
                                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Users who notified</p>
                                  {loadingRegsId === item._id ? (
                                    <p className="text-sm text-gray-500">Loading notify list…</p>
                                  ) : regs.length === 0 ? (
                                    <p className="text-sm text-gray-500">No one has tapped NOTIFY ME yet.</p>
                                  ) : (
                                    <table className="w-full text-xs bg-white rounded-lg overflow-hidden">
                                      <thead>
                                        <tr className="text-left text-gray-500">
                                          <th className="px-3 py-2">Name</th>
                                          <th className="px-3 py-2">Email / phone</th>
                                          <th className="px-3 py-2">Registered at</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {regs.map((row) => (
                                          <tr key={String(row.user_id)} className="border-t">
                                            <td className="px-3 py-2">{row.name || '—'}</td>
                                            <td className="px-3 py-2">{row.email || row.phone || '—'}</td>
                                            <td className="px-3 py-2">{formatWhen(row.registered_at)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {totalPages > 1 && (
                <div className="px-4 py-3 flex justify-end gap-1 border-t">
                  <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg disabled:opacity-40">
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg disabled:opacity-40">
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b font-bold">{isEditing ? 'Edit Coming soon' : 'Add Coming soon'}</div>
            <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1 text-sm">
              {formGeneralError && <div className="p-3 bg-red-50 text-red-700 rounded-lg">{formGeneralError}</div>}
              <label className="block text-xs font-medium">Title *</label>
              <input className="w-full border rounded-lg px-3 py-2" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
              {formErrors.title && <p className="text-xs text-red-600">{formErrors.title}</p>}
              <label className="block text-xs font-medium">Description *</label>
              <textarea className="w-full border rounded-lg px-3 py-2" rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              {formErrors.description && <p className="text-xs text-red-600">{formErrors.description}</p>}
              <ComingSoonImageField
                id="coming-soon-image"
                value={formData.image}
                error={formErrors.image}
                resetKey={imageFieldResetKey}
                disabled={saving}
                onUploadingChange={setUploadingImage}
                onChange={(image) => {
                  setFormData((prev) => ({ ...prev, image }));
                  if (formErrors.image) setFormErrors((prev) => ({ ...prev, image: '' }));
                }}
                onClear={() => {
                  setFormData((prev) => ({ ...prev, image: '' }));
                  if (formErrors.image) setFormErrors((prev) => ({ ...prev, image: '' }));
                }}
              />
            </div>
            <div className="px-6 py-4 flex items-center justify-between gap-3 border-t border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-700">Active</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.is_active}
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  disabled={saving || uploadingImage}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                    formData.is_active ? 'bg-brand' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving || uploadingImage}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || uploadingImage}
                  className="px-4 py-2 text-sm text-white rounded-lg bg-brand disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="font-semibold text-gray-800 mb-2">Deactivate this feature?</h3>
            <p className="text-sm text-gray-500 mb-4">It is hidden on the app. Notify rows are kept — no hard delete.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowDeactivateModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={confirmDeactivate} disabled={deactivating} className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg">Deactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComingSoonTab;
